import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../utils/constants';
import apiService from '../services/api';

const NotificationsPage = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read

  useEffect(() => {
    loadNotifications();
  }, []);

  // Auto-mark all notifications as read after 2 seconds of viewing
  useEffect(() => {
    if (notifications.length > 0 && notifications.some(n => !n.read)) {
      const timer = setTimeout(() => {
        markAllAsRead();
      }, 2000); // 2 seconds delay

      return () => clearTimeout(timer);
    }
  }, [notifications]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      
      // Load read notifications from localStorage
      const readNotificationIds = JSON.parse(localStorage.getItem('readNotifications') || '[]');
      
      // Generate notifications from real user data
      const generatedNotifications = [];
      
      // 1. Get user bundles and generate bundle purchase notifications
      try {
        const bundles = await apiService.getUserBundles();
        bundles.forEach((bundle) => {
          const notifId = `bundle-${bundle.id}`;
          generatedNotifications.push({
            id: notifId,
            type: 'bundle_purchase',
            title: 'تم شراء حزمة جديدة',
            message: `تم شراء حزمة تحتوي على ${bundle.item_count} محتويات بنجاح! وفرت ${bundle.discount_amount} دج (${bundle.discount_percent}% خصم)`,
            link: '/bundles',
            read: readNotificationIds.includes(notifId),
            created_at: bundle.created_at
          });
        });
      } catch (err) {
        console.error('Error loading bundles for notifications:', err);
      }
      
      // 2. Get user purchases and generate purchase confirmation notifications (only for non-bundle purchases)
      try {
        const purchases = await apiService.getUserPurchases();
        purchases.forEach((purchase) => {
          // Skip bundle purchases since we show them above
          if (purchase.purchase_type === 'bundle') return;
          
          const notifId = `purchase-${purchase.id}`;
          const courseTitle = purchase.title_ar || purchase.title || 'محتوى';
          generatedNotifications.push({
            id: notifId,
            type: 'purchase_confirmation',
            title: 'تم شراء محتوى جديد',
            message: `تم شراء "${courseTitle}" بنجاح. يمكنك البدء في التعلم الآن!`,
            link: purchase.content_type === 'course' ? `/course/${purchase.id}` : `/storybooks/${purchase.id}`,
            read: readNotificationIds.includes(notifId),
            created_at: purchase.purchased_at || purchase.created_at
          });
        });
      } catch (err) {
        console.error('Error loading purchases for notifications:', err);
      }

      // 3. Get manual payments and generate payment status notifications
      try {
        const payments = await apiService.getUserManualPayments();
        payments.forEach((payment) => {
          if (payment.status === 'approved') {
            const notifId = `payment-approved-${payment.id}`;
            
            // Determine message and link based on purchase type
            let message, link;
            if (payment.purchase_type === 'bundle') {
              const itemCount = payment.item_ids ? JSON.parse(payment.item_ids).length : 0;
              message = `تم قبول طلب الدفع للحزمة: "${payment.course_title}" (${itemCount} محتويات). يمكنك الآن الوصول لجميع المحتويات!`;
              link = '/bundles';
            } else if (payment.purchase_type === 'subscription') {
              message = `تم قبول طلب الدفع للاشتراك الشهري. يمكنك الآن الوصول لجميع المحتويات!`;
              link = '/dashboard';
            } else {
              message = `تم قبول طلب الدفع للمحتوى: "${payment.course_title}". يمكنك الآن الوصول للمحتوى!`;
              link = `/course/${payment.course_id}`;
            }
            
            generatedNotifications.push({
              id: notifId,
              type: 'payment_approved',
              title: 'تم قبول طلب الدفع اليدوي ✓',
              message: message,
              link: link,
              read: readNotificationIds.includes(notifId),
              created_at: payment.updated_at || payment.created_at
            });
          } else if (payment.status === 'rejected') {
            const notifId = `payment-rejected-${payment.id}`;
            
            // Determine message based on purchase type
            let message;
            if (payment.purchase_type === 'bundle') {
              const itemCount = payment.item_ids ? JSON.parse(payment.item_ids).length : 0;
              message = `تم رفض طلب الدفع للحزمة: "${payment.course_title}" (${itemCount} محتويات). ${payment.admin_notes ? `السبب: ${payment.admin_notes}` : 'يرجى التواصل مع الدعم للمزيد من المعلومات.'}`;
            } else if (payment.purchase_type === 'subscription') {
              message = `تم رفض طلب الدفع للاشتراك الشهري. ${payment.admin_notes ? `السبب: ${payment.admin_notes}` : 'يرجى التواصل مع الدعم للمزيد من المعلومات.'}`;
            } else {
              message = `تم رفض طلب الدفع للمحتوى: "${payment.course_title}". ${payment.admin_notes ? `السبب: ${payment.admin_notes}` : 'يرجى التواصل مع الدعم للمزيد من المعلومات.'}`;
            }
            
            generatedNotifications.push({
              id: notifId,
              type: 'payment_rejected',
              title: 'تم رفض طلب الدفع اليدوي ✗',
              message: message,
              link: '/dashboard',
              read: readNotificationIds.includes(notifId),
              created_at: payment.updated_at || payment.created_at
            });
          }
        });
      } catch (err) {
        console.error('Error loading manual payments for notifications:', err);
      }

      // 3. Check subscription status and generate expiry warning if needed
      try {
        const subStatus = await apiService.getSubscriptionStatus();
        if (subStatus && subStatus.is_active && subStatus.next_payment_date) {
          const nextPayment = new Date(subStatus.next_payment_date);
          const today = new Date();
          const daysUntilExpiry = Math.ceil((nextPayment - today) / (1000 * 60 * 60 * 24));
          
          // Warn if 3 days or less until expiry
          if (daysUntilExpiry <= 3 && daysUntilExpiry > 0) {
            const notifId = `subscription-expiry-warning`;
            generatedNotifications.push({
              id: notifId,
              type: 'subscription_expiry',
              title: 'تنبيه: اشتراكك سينتهي قريباً ⚠️',
              message: `اشتراكك الشهري سينتهي بعد ${daysUntilExpiry} ${daysUntilExpiry === 1 ? 'يوم' : 'أيام'}. جدد اشتراكك للحفاظ على الوصول لجميع المحتويات.`,
              link: '/pricing',
              read: readNotificationIds.includes(notifId),
              created_at: new Date().toISOString()
            });
          }
        }
      } catch (err) {
        console.log('No active subscription or error checking:', err);
      }

      // Sort notifications by date (newest first)
      generatedNotifications.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );

      setNotifications(generatedNotifications);
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      // Update state
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      
      // Save to localStorage
      const readNotificationIds = JSON.parse(localStorage.getItem('readNotifications') || '[]');
      if (!readNotificationIds.includes(notificationId)) {
        readNotificationIds.push(notificationId);
        localStorage.setItem('readNotifications', JSON.stringify(readNotificationIds));
      }
      
      // Trigger header refresh
      window.dispatchEvent(new Event('notificationsRead'));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      // Update state
      const updatedNotifications = notifications.map(n => ({ ...n, read: true }));
      setNotifications(updatedNotifications);
      
      // Save all notification IDs to localStorage
      const allNotificationIds = updatedNotifications.map(n => n.id);
      localStorage.setItem('readNotifications', JSON.stringify(allNotificationIds));
      
      // Trigger header to refresh unread count
      window.dispatchEvent(new Event('notificationsRead'));
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      // TODO: API call to delete notification
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_content':
        return { icon: 'fa-sparkles', color: 'from-blue-500 to-indigo-600' };
      case 'payment_approved':
        return { icon: 'fa-check-circle', color: 'from-green-500 to-emerald-600' };
      case 'payment_rejected':
        return { icon: 'fa-times-circle', color: 'from-red-500 to-red-600' };
      case 'subscription_expiry':
        return { icon: 'fa-exclamation-triangle', color: 'from-orange-500 to-red-600' };
      case 'purchase_confirmation':
        return { icon: 'fa-shopping-bag', color: 'from-purple-500 to-pink-600' };
      default:
        return { icon: 'fa-bell', color: 'from-gray-500 to-gray-600' };
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'read') return n.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="relative z-10 min-h-screen flex items-center justify-center bg-bg">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text3">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative z-10 py-12 md:py-20 pb-24 md:pb-20 bg-gradient-to-b from-bg to-white min-h-screen">
      <div className="container mx-auto px-3 md:px-4">
        <div className="max-w-[900px] mx-auto">
          {/* Header */}
          <div className="mb-12">
            <Link
              to={ROUTES.DASHBOARD}
              className="inline-flex items-center gap-2 text-primary hover:underline font-bold mb-4"
            >
              <i className="fas fa-arrow-right"></i>
              العودة للوحة التحكم
            </Link>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-4xl md:text-5xl font-black text-text mb-2 flex items-center gap-3">
                  <i className="fas fa-bell text-primary"></i>
                  الإشعارات
                </h1>
                <p className="text-lg text-text3">
                  {unreadCount > 0 ? `لديك ${unreadCount} إشعار غير مقروء` : 'لا توجد إشعارات غير مقروءة'}
                </p>
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="px-4 py-2 text-primary hover:bg-primary/10 rounded-lg font-bold transition-colors"
                >
                  تحديد الكل كمقروء
                </button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-3 mb-8">
            <button
              onClick={() => setFilter('all')}
              className={`px-6 py-2 rounded-xl font-bold transition-colors ${
                filter === 'all'
                  ? 'bg-primary text-white'
                  : 'bg-white text-text2 hover:bg-gray-50'
              }`}
            >
              الكل ({notifications.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-6 py-2 rounded-xl font-bold transition-colors ${
                filter === 'unread'
                  ? 'bg-primary text-white'
                  : 'bg-white text-text2 hover:bg-gray-50'
              }`}
            >
              غير مقروءة ({unreadCount})
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`px-6 py-2 rounded-xl font-bold transition-colors ${
                filter === 'read'
                  ? 'bg-primary text-white'
                  : 'bg-white text-text2 hover:bg-gray-50'
              }`}
            >
              مقروءة ({notifications.length - unreadCount})
            </button>
          </div>

          {/* Notifications List */}
          {filteredNotifications.length > 0 ? (
            <div className="space-y-4">
              {filteredNotifications.map((notification) => {
                const { icon, color } = getNotificationIcon(notification.type);
                return (
                  <div
                    key={notification.id}
                    className={`rounded-2xl p-6 shadow-custom hover:shadow-custom-lg transition-all duration-700 ease-in-out ${
                      !notification.read 
                        ? 'bg-gradient-to-br from-blue-50 to-white' 
                        : 'bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`w-12 h-12 bg-gradient-to-br ${color} rounded-xl flex items-center justify-center text-white flex-shrink-0 transition-opacity duration-700 ${
                        notification.read ? 'opacity-70' : 'opacity-100'
                      }`}>
                        <i className={`fas ${icon} text-xl`}></i>
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <h3 className={`text-xl font-bold transition-colors duration-700 ${notification.read ? 'text-text2' : 'text-text'}`}>
                            {notification.title}
                          </h3>
                          <div className="relative min-w-[60px] h-[28px]">
                            <span 
                              className={`absolute top-0 right-0 px-3 py-1 text-xs font-bold rounded-full transition-all duration-700 ${
                                !notification.read 
                                  ? 'bg-primary text-white animate-pulse opacity-100 scale-100' 
                                  : 'bg-primary text-white opacity-0 scale-75'
                              }`}
                            >
                              جديد
                            </span>
                            <span 
                              className={`absolute top-0 right-0 px-3 py-1 text-xs font-bold rounded-full transition-all duration-700 ${
                                notification.read 
                                  ? 'bg-gray-200 text-gray-500 opacity-100 scale-100' 
                                  : 'bg-gray-200 text-gray-500 opacity-0 scale-75'
                              }`}
                            >
                              مقروء
                            </span>
                          </div>
                        </div>
                        <p className={`mb-3 transition-colors duration-700 ${notification.read ? 'text-text2' : 'text-text2'}`}>
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-text3">
                            {new Date(notification.created_at).toLocaleDateString('ar-DZ', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          <div className="flex items-center gap-2">
                            {notification.link && (
                              <Link
                                to={notification.link}
                                onClick={() => !notification.read && markAsRead(notification.id)}
                                className="text-primary hover:underline font-bold text-sm"
                              >
                                عرض التفاصيل →
                              </Link>
                            )}
                            <button
                              onClick={() => deleteNotification(notification.id)}
                              className="text-sm text-text3 hover:text-red-600"
                              title="حذف"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-12 text-center shadow-custom">
              <i className="fas fa-bell-slash text-6xl text-text3 mb-4"></i>
              <h3 className="text-2xl font-bold text-text mb-2">لا توجد إشعارات</h3>
              <p className="text-text3">
                {filter === 'unread' ? 'جميع إشعاراتك مقروءة' : 'لم تصلك أي إشعارات بعد'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Navigation - Mobile Only */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50 safe-area-bottom">
        <div className="grid grid-cols-3 gap-1 px-2 py-1">
          <Link
            to="/notifications"
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg bg-primary text-white transition-colors"
          >
            <i className="fas fa-bell text-lg mb-0.5"></i>
            <span className="text-[9px] font-bold">الإشعارات</span>
            {unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </Link>
          
          <Link
            to={ROUTES.DASHBOARD}
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors relative"
          >
            <i className="fas fa-home text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">لوحة التحكم</span>
          </Link>
          
          <Link
            to="/profile"
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <i className="fas fa-user text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">الملف الشخصي</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
